<template>
  <div class="transcoding-config space-y-4 text-xs">
    <!-- Name -->
    <div class="space-y-1.5">
      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest leading-none mb-1">Name</label>
      <input
        v-model="config.name"
        type="text"
        placeholder="Profile Name"
        class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
        @input="emitUpdate"
      />
    </div>

    <!-- Basic Info -->
    <div class="grid grid-cols-2 gap-4">
      <div class="space-y-1.5">
        <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest leading-none mb-1">Channel Class</label>
        <select
          v-model="config.channelClass"
          class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] cursor-pointer"
          @change="emitUpdate"
        >
          <option value="STANDARD">STANDARD</option>
          <option value="SINGLE_PIPELINE">SINGLE_PIPELINE</option>
        </select>
      </div>
      <div class="space-y-1.5">
        <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest leading-none mb-1">Input Type</label>
        <select
          v-model="config.inputType"
          class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] cursor-pointer"
          @change="emitUpdate"
        >
          <option value="MEDIACONNECT_ROUTER">MEDIACONNECT_ROUTER</option>
        </select>
      </div>
    </div>

    <div
      v-if="config.channelClass === 'STANDARD'"
      class="rounded-lg border border-yellow-700/50 bg-yellow-900/20 px-3 py-2 text-xs text-yellow-300">
      Warning: dual pipeline channels are not supported yet in UI automation. Save is allowed, but setup may require manual backend handling.
    </div>

    <!-- Input Specification (Optional) -->
    <div class="rounded-sm p-4 bg-black/30">
      <div class="flex items-center justify-between mb-3">
        <span class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Input Specification</span>
        <button
          v-if="!config.InputSpecification"
          class="text-[9px] bg-[#ffaa00]/15 hover:bg-[#ffaa00]/25 text-[#ffaa00] px-3 py-1 rounded-sm transition-all font-black uppercase tracking-widest border border-[#ffaa00]/30"
          @click="addInputSpec"
        >
          + Add Spec
        </button>
        <ma-delete
          v-else
          class="bly-delete ma-btn hover:text-yellow-500"
          @click="removeInputSpec"
        ></ma-delete>
      </div>

      <div v-if="config.InputSpecification" class="grid grid-cols-3 gap-3 animate-in fade-in zoom-in-95 duration-200">
        <div class="space-y-1.5">
          <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest mb-1">Codec</label>
          <select
            v-model="config.InputSpecification.Codec"
            class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] cursor-pointer"
            @change="emitUpdate"
          >
            <option value="AVC">AVC</option>
            <option value="HEVC">HEVC</option>
            <option value="MPEG2">MPEG2</option>
          </select>
        </div>
        <div class="space-y-1.5">
          <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest mb-1">Max Bitrate</label>
          <select
            v-model="config.InputSpecification.MaximumBitrate"
            class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] cursor-pointer"
            @change="emitUpdate"
          >
            <option value="MAX_10_MBPS">MAX_10_MBPS</option>
            <option value="MAX_20_MBPS">MAX_20_MBPS</option>
            <option value="MAX_50_MBPS">MAX_50_MBPS</option>
          </select>
        </div>
        <div class="space-y-1.5">
          <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest mb-1">Resolution</label>
          <select
            v-model="config.InputSpecification.Resolution"
            class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] cursor-pointer"
            @change="emitUpdate"
          >
            <option value="SD">SD</option>
            <option value="HD">HD</option>
            <option value="UHD">UHD</option>
          </select>
        </div>
      </div>
      <div v-else class="text-center py-2 text-[10px] text-[#8f9bb2] uppercase font-black tracking-widest">
        Standard specification used
      </div>
    </div>

    <!-- Default Destination (Optional) -->
    <div class="rounded-sm p-4 bg-black/30">
      <div class="flex items-center justify-between mb-3">
        <span class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Default Destination</span>
        <button
          v-if="!config.defaultDestinations || !config.defaultDestinations.length"
          class="text-[9px] bg-[#ffaa00]/15 hover:bg-[#ffaa00]/25 text-[#ffaa00] px-3 py-1 rounded-sm transition-all font-black uppercase tracking-widest border border-[#ffaa00]/30"
          @click="addDestination"
        >
          + Add Destination
        </button>
        <ma-delete
          v-else
          class="bly-delete ma-btn"
          @click="removeDestination(0)"
        ></ma-delete>
      </div>

      <div v-if="config.defaultDestinations && config.defaultDestinations.length" class="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
        <div
          v-for="(dest, dIdx) in config.defaultDestinations"
          :key="dIdx"
        >
          <div class="mb-2 space-y-1.5">
            <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Destination ID</label>
            <input
              v-model="dest.Id"
              type="text"
              class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/30 px-2.5 text-xs text-[#ffaa00] font-mono transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
              @input="emitUpdate"
            />
          </div>

          <div v-for="(setting, sIdx) in dest.Settings" :key="sIdx" class="mt-2 space-y-1.5">
            <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Url {{ sIdx + 1 }}</label>
            <input
              v-model="setting.Url"
              type="text"
              placeholder="s3://path/to/recording"
              class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/30 px-2.5 text-xs text-[#e5eefc] font-mono transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
              @input="emitUpdate"
            />
          </div>
        </div>
      </div>
      <div v-else class="text-center py-2 text-[10px] text-[#8f9bb2] uppercase font-black tracking-widest">
        No default destination
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TranscodingConfigEditor',
  props: {
    value: {
      type: [Object, String],
      default: () => ({})
    }
  },
  data() {
    let parsed = this.value;
    if (typeof this.value === 'string') {
      try {
        parsed = JSON.parse(this.value);
      } catch (e) {
        parsed = {};
      }
    }

    return {
      config: {
        name: parsed.name || '',
        channelClass: parsed.channelClass || 'SINGLE_PIPELINE',
        inputType: parsed.inputType || 'MEDIACONNECT_ROUTER',
        defaultDestinations: parsed.defaultDestinations || [],
        InputSpecification: parsed.InputSpecification || null,
        ...parsed
      }
    };
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        // Avoid infinite loop by only updating if actually different
        const incoming = typeof newVal === 'string' ? JSON.parse(newVal || '{}') : newVal;
        if (JSON.stringify(incoming) !== JSON.stringify(this.config)) {
          this.config = { ...this.config, ...incoming };
        }
      }
    }
  },
  methods: {
    emitUpdate() {
      this.$emit('input', JSON.stringify(this.config));
    },
    addInputSpec() {
      this.config.InputSpecification = {
        Codec: 'AVC',
        MaximumBitrate: 'MAX_20_MBPS',
        Resolution: 'HD'
      };
      this.emitUpdate();
    },
    removeInputSpec() {
      this.config.InputSpecification = null;
      this.emitUpdate();
    },
    addDestination() {
      if (!this.config.defaultDestinations) {
        this.$set(this.config, 'defaultDestinations', []);
      }
      if (this.config.defaultDestinations.length >= 1) return;

      this.config.defaultDestinations.push({
        Id: 'dest-1',
        Settings: [
          { Url: '' },
          { Url: '' }
        ]
      });
      this.emitUpdate();
    },
    removeDestination(index) {
      this.config.defaultDestinations.splice(index, 1);
      this.emitUpdate();
    }
  }
};
</script>
